import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Zap, MessageSquare, CheckCircle, Users, ShieldCheck, AlertTriangle, FileEdit, MousePointerClick, KeyRound, Smartphone, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatusRow = ({ label, value, ok }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-slate-500">{label}</span>
    <span className={`inline-flex items-center gap-1.5 font-medium ${ok ? 'text-emerald-600' : 'text-slate-600'}`}>
      <span className={`h-2 w-2 rounded-full ${ok ? 'bg-emerald-500' : 'bg-slate-300'}`} />
      {value}
    </span>
  </div>
);

const WhatsAppAutomation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const meRole = user?.role;
  const [leadAuto, setLeadAuto] = useState(null);
  const [savingLeadAuto, setSavingLeadAuto] = useState(false);
  const [otp, setOtp] = useState(null);
  const [savingOtp, setSavingOtp] = useState(false);
  const [testChannel, setTestChannel] = useState('whatsapp');
  const [testContact, setTestContact] = useState('');
  const [testSending, setTestSending] = useState(false);

  useEffect(() => {
    if (meRole && meRole !== 'admin') navigate('/whatsapp');
  }, [meRole, navigate]);

  const fetchLeadAutomation = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/whatsapp/lead-automation`);
      setLeadAuto(res.data);
    } catch (error) {
      console.debug('Lead automation fetch failed (non-critical):', error?.message || error);
    }
  };

  const fetchOtp = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/whatsapp/otp-verification`);
      setOtp(res.data);
    } catch (error) {
      console.debug('OTP verification fetch failed (non-critical):', error?.message || error);
    }
  };

  useEffect(() => { fetchLeadAutomation(); fetchOtp(); /* eslint-disable-next-line */ }, []);

  const handleToggleOtp = async (enabled) => {
    setSavingOtp(true);
    try {
      await axios.put(`${BACKEND_URL}/api/whatsapp/otp-verification`, { enabled });
      await fetchOtp();
      toast.success(enabled ? 'OTP verification enabled' : 'OTP verification disabled');
    } catch (error) {
      toast.error('Could not update OTP verification');
    } finally {
      setSavingOtp(false);
    }
  };

  const handleSubmitOtpTemplate = async () => {
    setSavingOtp(true);
    try {
      await axios.post(`${BACKEND_URL}/api/whatsapp/otp-template/submit`);
      await fetchOtp();
      toast.success('OTP template submitted to Meta — approval can take up to 24 hours.');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 503) {
        toast.error('WhatsApp is not connected yet — connect Meta before submitting.');
      } else {
        toast.error(detail || 'Could not submit the OTP template to Meta');
      }
    } finally {
      setSavingOtp(false);
    }
  };

  const handleSyncOtpTemplate = async () => {
    if (!otp?.otpTemplateDbId) return;
    setSavingOtp(true);
    try {
      await axios.post(`${BACKEND_URL}/api/whatsapp/templates/${otp.otpTemplateDbId}/sync-status`);
      await fetchOtp();
      toast.success('Synced OTP template status from Meta.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not sync from Meta');
    } finally {
      setSavingOtp(false);
    }
  };

  const handleSendTestCode = async () => {
    const contact = testContact.trim();
    if (!contact) {
      toast.error('Enter a phone number or email to test');
      return;
    }
    setTestSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/whatsapp/otp-test-send`, { channel: testChannel, contact });
      toast.success(`Test code sent via ${testChannel === 'whatsapp' ? 'WhatsApp' : 'email'} — check ${contact}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not send test code');
    } finally {
      setTestSending(false);
    }
  };

  const otpChannelLabel = () => {
    if (!otp) return '—';
    if (otp.whatsappReady) return 'Ready';
    if (otp.whatsappReadyReason === 'not_connected') return 'Not connected';
    if (otp.whatsappReadyReason === 'template_name_missing') return 'Connected · server config missing';
    if (otp.whatsappReadyReason === 'template_not_approved') return 'Connected · template not active yet';
    return otp.whatsappConfigured ? 'Connected' : 'Not connected';
  };

  const handleToggleLeadAutomation = async (enabled) => {
    setSavingLeadAuto(true);
    try {
      await axios.put(`${BACKEND_URL}/api/whatsapp/lead-automation`, { autoReplyEnabled: enabled });
      await fetchLeadAutomation();
      toast.success(enabled ? 'Lead auto-reply enabled' : 'Lead auto-reply disabled');
    } catch (error) {
      toast.error('Could not update lead automation');
    } finally {
      setSavingLeadAuto(false);
    }
  };

  const handleUpdateLeadAuto = async (patch, successMsg) => {
    setSavingLeadAuto(true);
    try {
      await axios.put(`${BACKEND_URL}/api/whatsapp/lead-automation`, patch);
      await fetchLeadAutomation();
      if (successMsg) toast.success(successMsg);
    } catch (error) {
      toast.error('Could not update lead automation');
    } finally {
      setSavingLeadAuto(false);
    }
  };

  const handleSubmitCaptureTemplate = async () => {
    setSavingLeadAuto(true);
    try {
      await axios.post(`${BACKEND_URL}/api/whatsapp/lead-automation/submit-template`);
      await fetchLeadAutomation();
      toast.success('Submitted to Meta — approval usually takes a few minutes to a few hours.');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 503) {
        toast.error('WhatsApp is not connected yet — connect Meta before submitting.');
      } else {
        toast.error(detail || 'Could not submit the template to Meta');
      }
    } finally {
      setSavingLeadAuto(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-2xl" data-testid="whatsapp-automation-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Zap className="h-6 w-6 text-primary-600" /> Lead Automation
        </h1>
        <p className="text-sm text-slate-500 mt-1">Automatically greet new WhatsApp leads and capture trip details from their replies.</p>
      </div>

      {/* Auto first-reply */}
      <Card className="rounded-2xl border border-slate-200 p-5 space-y-5" data-testid="lead-automation-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="rounded-xl bg-primary-50 p-2.5 text-primary-600"><MessageSquare className="h-5 w-5" /></span>
            <div>
              <p className="text-sm font-semibold text-slate-900">Auto first-reply to new leads</p>
              <p className="text-sm text-slate-500 mt-0.5">Greet every new lead on WhatsApp and start capturing their trip details. Manual/website leads get the approved “Lead enquiry capture” template; leads who message you first get a guided button flow (below).</p>
            </div>
          </div>
          <Switch
            data-testid="lead-automation-toggle"
            checked={!!leadAuto?.autoReplyEnabled}
            disabled={savingLeadAuto}
            onCheckedChange={handleToggleLeadAutomation}
          />
        </div>

        <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 space-y-2" data-testid="lead-automation-status">
          <StatusRow label="Template status" ok={leadAuto?.templateApproved}
            value={leadAuto?.templateApproved ? 'Approved' : (leadAuto?.templateStatus || 'Not created')} />
          <StatusRow label="WhatsApp connection" ok={leadAuto?.whatsappConfigured}
            value={leadAuto?.whatsappConfigured ? 'Connected' : 'Not connected'} />
          <StatusRow label="Live sending" ok={leadAuto?.wouldSendLive}
            value={leadAuto?.wouldSendLive ? 'Active — will send to new leads' : 'Inactive'} />
        </div>

        {/* Edit template in the main library (Task 1b) */}
        <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3" data-testid="lead-template-edit-row">
          <p className="text-sm text-slate-600">The greeting text is a normal template — edit or version it in your template library.</p>
          <Button variant="outline" size="sm" className="shrink-0 rounded-xl" data-testid="lead-template-edit-btn"
            onClick={() => navigate('/whatsapp/templates')}>
            <FileEdit className="h-3.5 w-3.5 mr-1" /> Edit in Message Templates
          </Button>
        </div>

        {/* Submit-to-Meta for the capture template (shown until Approved) */}
        {leadAuto && !leadAuto.templateApproved && (
          <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="lead-template-submit-box">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-900">“Lead enquiry capture” template</p>
                <p className="text-sm text-slate-500 mt-0.5">
                  {leadAuto.templatePending
                    ? 'Submitted to Meta — waiting for approval. This can take a few minutes to a few hours.'
                    : 'This template must be approved by Meta before auto-reply can send. It is separate from your other approved templates.'}
                </p>
                {leadAuto.templateRejectionReason && (
                  <p className="text-sm text-red-600 mt-1" data-testid="lead-template-reject-reason">Rejected by Meta: {leadAuto.templateRejectionReason}</p>
                )}
                {leadAuto.templateSubmissionError && (
                  <p className="text-sm text-red-600 mt-1" data-testid="lead-template-submit-error">Submission error: {leadAuto.templateSubmissionError}</p>
                )}
              </div>
              {leadAuto.templatePending ? (
                <span className="shrink-0 rounded-full bg-amber-100 text-amber-700 text-xs font-medium px-3 py-1" data-testid="lead-template-pending-badge">Pending Meta</span>
              ) : (
                <Button size="sm" variant="outline" className="shrink-0 rounded-xl" data-testid="lead-template-submit-btn"
                  disabled={savingLeadAuto || !leadAuto.whatsappConfigured || !leadAuto.templateSubmittable}
                  onClick={handleSubmitCaptureTemplate}>
                  Submit to Meta for approval
                </Button>
              )}
            </div>
            {!leadAuto.whatsappConfigured && (
              <p className="text-xs text-slate-500" data-testid="lead-template-connect-hint">Connect WhatsApp (Meta) first, then submit this template for approval.</p>
            )}
          </div>
        )}

        {!leadAuto?.wouldSendLive && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="lead-automation-hint">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              Even with the toggle on, no live message is sent until the “Lead enquiry capture” template is <strong>Approved</strong> by Meta and WhatsApp is <strong>Connected</strong>. Rule-based capture from replies keeps working regardless.
            </p>
          </div>
        )}
      </Card>

      {/* Guided flow explainer (Task 2) */}
      <Card className="rounded-2xl border border-slate-200 p-5 mt-5" data-testid="guided-flow-card">
        <div className="flex items-start gap-3">
          <span className="rounded-xl bg-primary-50 p-2.5 text-primary-600"><MousePointerClick className="h-5 w-5" /></span>
          <div>
            <p className="text-sm font-semibold text-slate-900">Guided button capture (for leads who message first)</p>
            <p className="text-sm text-slate-500 mt-0.5">When someone messages your number, the bot runs a short 3-message flow, then hands off to an agent:</p>
            <ol className="mt-2 space-y-1 text-sm text-slate-600 list-decimal pl-5">
              <li>Greeting + trip-type buttons: <strong>Family Trip · Honeymoon · Group / Other</strong></li>
              <li>Traveller count buttons: <strong>1-2 · 3-5 · 6+</strong></li>
              <li>One free-text ask for destination, dates &amp; departure city</li>
            </ol>
            <p className="text-xs text-slate-500 mt-2">If a lead types instead of tapping, their text is still parsed for the same details. After the 3rd message the bot stops and the lead is handed to an agent.</p>
          </div>
        </div>
      </Card>

      {/* Returning-traveller greeting (BATCH 5 · Session 3) — separate toggle,
          independent of the new-lead auto-reply flag above. */}
      <Card className="rounded-2xl border border-slate-200 p-5 mt-5" data-testid="returning-greeting-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="rounded-xl bg-primary-50 p-2.5 text-primary-600"><MessageSquare className="h-5 w-5" /></span>
            <div>
              <p className="text-sm font-semibold text-slate-900">Returning-traveller greeting</p>
              <p className="text-sm text-slate-500 mt-0.5">When a known traveller with at least one confirmed past trip messages in (and has no open lead), skip the new-lead bot and send a personalized "Welcome back" message referencing their last destination — instead of the generic intake flow. Routes straight to their owning agent (or round-robin if none).</p>
            </div>
          </div>
          <Switch
            data-testid="returning-greeting-toggle"
            checked={!!leadAuto?.returningGreetingEnabled}
            disabled={savingLeadAuto}
            onCheckedChange={(enabled) => handleUpdateLeadAuto(
              { returningGreetingEnabled: enabled },
              enabled ? 'Returning-traveller greeting enabled' : 'Returning-traveller greeting disabled',
            )}
          />
        </div>
      </Card>

      {/* OTP verification for website lead capture */}
      <Card className="rounded-2xl border border-slate-200 p-5 space-y-5 mt-5" data-testid="otp-verification-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="rounded-xl bg-primary-50 p-2.5 text-primary-600"><KeyRound className="h-5 w-5" /></span>
            <div>
              <p className="text-sm font-semibold text-slate-900">Website lead OTP verification</p>
              <p className="text-sm text-slate-500 mt-0.5">Before a lead from the website quiz is saved, verify the person's WhatsApp number or email with a one-time code. Blocks fake/typo contacts at the door. The website calls two endpoints — <code className="text-xs">/api/public/otp/send</code> then <code className="text-xs">/api/public/otp/verify</code>.</p>
            </div>
          </div>
          <Switch
            data-testid="otp-verification-toggle"
            checked={!!otp?.enabled}
            disabled={savingOtp}
            onCheckedChange={handleToggleOtp}
          />
        </div>

        <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 space-y-2" data-testid="otp-verification-status">
          <StatusRow label="WhatsApp OTP template" ok={otp?.templateApproved}
            value={otp?.templateApproved ? 'Approved' : (otp?.templateStatus || 'Not created')} />
          <StatusRow label="WhatsApp channel" ok={otp?.whatsappReady}
            value={otpChannelLabel()} />
          <StatusRow label="Email channel (Resend)" ok={otp?.emailConfigured}
            value={otp?.emailConfigured ? 'Ready' : 'Not configured'} />
        </div>

        {/* The Authentication template — name, status, live message preview + sync.
            It lives here (not in the Message Templates library) because an
            AUTHENTICATION template is a distinct Meta type with Meta-managed text. */}
        {otp?.authTemplateName && (
          <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="otp-template-preview">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-slate-900">WhatsApp OTP template</span>
                <span className="text-[10px] uppercase font-semibold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">Authentication</span>
                <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded font-mono" data-testid="otp-template-name">{otp.authTemplateName}</code>
              </div>
              <div className="flex items-center gap-2">
                {otp.templateStatus && (
                  <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${otp.templateApproved ? 'text-emerald-600 bg-emerald-50' : 'text-amber-600 bg-amber-50'}`} data-testid="otp-template-status-badge">{otp.templateApproved ? 'Approved' : otp.templateStatus}</span>
                )}
                {otp.otpTemplateDbId && (
                  <Button size="sm" variant="outline" className="rounded-lg h-7 text-xs" data-testid="otp-template-sync-btn"
                    disabled={savingOtp || !otp.whatsappConfigured} onClick={handleSyncOtpTemplate}>
                    Sync from Meta
                  </Button>
                )}
              </div>
            </div>
            {/* WhatsApp message preview — exactly what Meta sends */}
            <div className="rounded-xl bg-[#e5ddd5] p-3" data-testid="otp-template-bubble">
              <div className="max-w-xs rounded-lg bg-white shadow-sm px-3 py-2 text-sm text-slate-800">
                <p><span className="font-mono font-semibold tracking-widest">482913</span> is your verification code.</p>
                <p className="text-slate-500 text-xs mt-1">For your security, do not share this code.</p>
                <p className="text-slate-400 text-[11px] mt-1">This code expires in 10 minutes.</p>
                <div className="mt-2 -mx-3 -mb-2 border-t border-slate-100 text-center text-[#027eb5] text-sm font-medium py-1.5">Copy code</div>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              This is the exact message WhatsApp sends. The wording of an <strong>Authentication</strong> template is <strong>fixed by Meta</strong> (the code + a security line + expiry + a Copy-code button) — it can't carry custom text, so the sample body sent earlier won't appear. Your <strong>email</strong> OTP uses the branded Resend email instead.
            </p>
          </div>
        )}

        {/* Precise blocker text when WhatsApp isn't Ready (sync button is in the preview above) */}
        {otp && (otp.whatsappReadyReason === 'template_name_missing' || otp.whatsappReadyReason === 'template_not_approved') && (
          <div className="rounded-xl border border-slate-200 p-4 space-y-2.5" data-testid="otp-whatsapp-blocker">
            {otp.whatsappReadyReason === 'template_name_missing' && (
              <p className="text-sm text-slate-600">The template is <strong>approved by Meta</strong>, but this server is missing the <code className="text-xs">WHATSAPP_AUTH_TEMPLATE_NAME</code> setting — <strong>redeploy</strong> production so it picks up the OTP config, then this will turn <span className="text-emerald-600 font-medium">Ready</span>.</p>
            )}
            {otp.whatsappReadyReason === 'template_not_approved' && (
              <p className="text-sm text-slate-600">Meta shows this template as approved but the CRM hasn't activated it yet. Click <strong>Sync from Meta</strong> above to refresh its status.</p>
            )}
          </div>
        )}

        {/* Submit AUTHENTICATION template to Meta — only when there's nothing approved/pending yet */}
        {otp && otp.templateSubmittable && (
          <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="otp-template-submit-box">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-900">WhatsApp Authentication template</p>
                <p className="text-sm text-slate-500 mt-0.5">
                  {otp.templatePending
                    ? 'Submitted to Meta — waiting for approval. Authentication templates can take up to 24 hours.'
                    : 'OTP over WhatsApp needs a Meta AUTHENTICATION template (a copy-code template, separate from your other templates). Email OTP works without it.'}
                </p>
                {otp.templateRejectionReason && (
                  <p className="text-sm text-red-600 mt-1" data-testid="otp-template-reject-reason">Rejected by Meta: {otp.templateRejectionReason}</p>
                )}
                {otp.templateSubmissionError && (
                  <p className="text-sm text-red-600 mt-1" data-testid="otp-template-submit-error">Submission error: {otp.templateSubmissionError}</p>
                )}
              </div>
              {otp.templatePending ? (
                <span className="shrink-0 rounded-full bg-amber-100 text-amber-700 text-xs font-medium px-3 py-1" data-testid="otp-template-pending-badge">Pending Meta</span>
              ) : (
                <Button size="sm" variant="outline" className="shrink-0 rounded-xl" data-testid="otp-template-submit-btn"
                  disabled={savingOtp || !otp.whatsappConfigured || !otp.templateSubmittable}
                  onClick={handleSubmitOtpTemplate}>
                  Submit to Meta for approval
                </Button>
              )}
            </div>
            {!otp.whatsappConfigured && (
              <p className="text-xs text-slate-500" data-testid="otp-template-connect-hint">Connect WhatsApp (Meta) first, then submit this template. You can use Email OTP in the meantime.</p>
            )}
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3" data-testid="otp-channel-cards">
          <div className="flex items-start gap-2 rounded-xl border border-slate-200 px-4 py-3">
            <Smartphone className="h-4 w-4 text-slate-500 shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600"><strong>WhatsApp</strong> — sends a copy-code message via the approved Authentication template.</p>
          </div>
          <div className="flex items-start gap-2 rounded-xl border border-slate-200 px-4 py-3">
            <Mail className="h-4 w-4 text-slate-500 shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600"><strong>Email</strong> — sends a branded code email via Resend.</p>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="otp-test-send-box">
          <p className="text-sm font-semibold text-slate-900">Send a test code to yourself</p>
          <p className="text-sm text-slate-500">Runs the exact same send pipeline as the website widget — an instant end-to-end check without touching the live quiz.</p>
          <div className="flex flex-wrap gap-2 items-center">
            <Select value={testChannel} onValueChange={setTestChannel}>
              <SelectTrigger className="w-36 rounded-xl" data-testid="otp-test-channel-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="email">Email</SelectItem>
              </SelectContent>
            </Select>
            <input
              type="text"
              className="flex-1 min-w-[180px] rounded-xl border border-slate-200 px-3 py-2 text-sm"
              placeholder={testChannel === 'whatsapp' ? '10-digit phone number' : 'you@example.com'}
              value={testContact}
              onChange={(e) => setTestContact(e.target.value)}
              data-testid="otp-test-contact-input"
            />
            <Button size="sm" className="rounded-xl" disabled={testSending} onClick={handleSendTestCode} data-testid="otp-test-send-btn">
              {testSending ? 'Sending…' : 'Send test code'}
            </Button>
          </div>
        </div>

        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="otp-verification-hint">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            Codes are single-use, channel-bound and valid for 10 minutes. Max 3 wrong attempts per code, and 5 sends per 15 minutes per contact. This gate only affects the <strong>website quiz path</strong> — WhatsApp inbound and manual leads are unaffected.
          </p>
        </div>
      </Card>
      <Card className="rounded-2xl border border-slate-200 p-5 space-y-5 mt-5" data-testid="lead-qualify-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="rounded-xl bg-primary-50 p-2.5 text-primary-600"><CheckCircle className="h-5 w-5" /></span>
            <div>
              <p className="text-sm font-semibold text-slate-900">Auto-qualify leads</p>
              <p className="text-sm text-slate-500 mt-0.5">When a lead's destination, travel dates and traveller count are all captured, move it to the <strong>Qualified</strong> stage automatically.</p>
            </div>
          </div>
          <Switch
            data-testid="lead-autoqualify-toggle"
            checked={!!leadAuto?.autoQualify}
            disabled={savingLeadAuto}
            onCheckedChange={(v) => handleUpdateLeadAuto({ autoQualify: v }, v ? 'Auto-qualify enabled' : 'Auto-qualify disabled')}
          />
        </div>

        <div className="flex items-start justify-between gap-4 border-t border-slate-100 pt-5">
          <div className="flex items-start gap-3">
            <span className="rounded-xl bg-primary-50 p-2.5 text-primary-600"><Users className="h-5 w-5" /></span>
            <div>
              <p className="text-sm font-semibold text-slate-900">Auto-assign qualified leads</p>
              <p className="text-sm text-slate-500 mt-0.5">Assign a newly-qualified lead to an agent so someone follows up. Pick a default owner, or leave it on round-robin across your agents.</p>
            </div>
          </div>
          <Switch
            data-testid="lead-autoassign-toggle"
            checked={!!leadAuto?.autoAssign}
            disabled={savingLeadAuto || !leadAuto?.autoQualify}
            onCheckedChange={(v) => handleUpdateLeadAuto({ autoAssign: v }, v ? 'Auto-assign enabled' : 'Auto-assign disabled')}
          />
        </div>

        {leadAuto?.autoAssign && (
          <div className="pl-14" data-testid="lead-assignee-row">
            <Label className="text-sm text-slate-600">Assign to</Label>
            <Select
              value={leadAuto?.assignToUserId || 'round_robin'}
              onValueChange={(v) => handleUpdateLeadAuto({ assignToUserId: v === 'round_robin' ? '' : v }, 'Assignee updated')}
            >
              <SelectTrigger className="h-11 rounded-xl mt-1.5 max-w-sm" data-testid="lead-assignee-select">
                <SelectValue placeholder="Round-robin (all agents)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="round_robin" data-testid="lead-assignee-opt-roundrobin">Round-robin (all agents)</SelectItem>
                {(leadAuto?.agents || []).map((a) => (
                  <SelectItem key={a.userId} value={a.userId} data-testid={`lead-assignee-opt-${a.userId}`}>
                    {a.name} · {a.role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid="lead-qualify-hint">
          <ShieldCheck className="h-4 w-4 text-slate-500 shrink-0 mt-0.5" />
          <p className="text-sm text-slate-600">
            Automation never overrides you: the instant an agent replies in a chat it <strong>pauses</strong> for that lead, and a customer who replies <strong>STOP</strong> is opted out of all further auto-messages.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default WhatsAppAutomation;
